import React, { useState, useEffect } from 'react';
import {
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_BASE } from '../../lib/config';

const APIKeyManagement = ({ onBack }) => {
  // Global Keys State
  const [keys, setKeys] = useState({
    google_maps_api_key: '',
    mapkit_js_api_key: '',
    stripe_secret_key: '',
    stripe_publishable_key: ''
  });

  const [envKeys, setEnvKeys] = useState({
    google_maps_api_key: '',
    mapkit_js_api_key: '',
    stripe_secret_key: '',
    stripe_publishable_key: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/admin/api-keys/global`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend returns flat keys + env object
      const { env, ...savedKeys } = res.data;
      setKeys(savedKeys);
      setEnvKeys(env || {});
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('APIキー情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKeys = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.put(`${API_BASE}/admin/api-keys/global`, keys, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('APIキー設定を保存しました');
      fetchKeys(); // Refresh to confirm
    } catch (error) {
      console.error('Save error:', error);
      toast.error('保存に失敗しました');
    }
  };

  const maskKey = (key) => {
    if (!key || key.length <= 8) return key;
    return key.substring(0, 8) + '...';
  };

  const renderKeyInput = (label, name, placeholderEnvKey, helpText) => {
    const envVal = envKeys[placeholderEnvKey];
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            value={keys[name]}
            onChange={(e) => setKeys({ ...keys, [name]: e.target.value })}
            placeholder={envVal ? `環境変数値を使用中 (${maskKey(envVal)})` : "APIキーを入力"}
            type={name.includes('secret') ? "password" : "text"}
          />
        </div>
        {envVal && (
          <p className="text-xs text-blue-600 flex items-center">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            システム環境変数が有効です
          </p>
        )}
        {helpText && (
          <p className="text-xs text-gray-500">{helpText}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">APIキー管理</h2>
          <p className="text-gray-600 mt-1">外部サービス連携の設定</p>
        </div>
        <button
          onClick={handleSaveKeys}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center shadow-sm"
        >
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          全設定を保存
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* マップ設定 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <GlobeAltIcon className="w-6 h-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">地図サービス (Maps)</h2>
              </div>
              <div className="space-y-6 max-w-3xl">
                {renderKeyInput(
                  "Google Maps API Key",
                  "google_maps_api_key",
                  "google_maps_api_key",
                  "空欄の場合は環境変数が使用されます。"
                )}
                {renderKeyInput(
                  "MapKit JS API Key (Apple)",
                  "mapkit_js_api_key",
                  "mapkit_js_api_key"
                )}
              </div>
            </div>
          </div>

          {/* 決済設定 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <CreditCardIcon className="w-6 h-6 text-green-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">決済サービス (Stripe)</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                決済機能に使用するStripeのキーを設定します。
              </p>
              <div className="space-y-6 max-w-3xl">
                {renderKeyInput(
                  "Stripe Publishable Key (公開キー)",
                  "stripe_publishable_key",
                  "stripe_publishable_key",
                  "フロントエンドで使用される公開キー (pk_...)"
                )}
                {renderKeyInput(
                  "Stripe Secret Key (秘密キー)",
                  "stripe_secret_key",
                  "stripe_secret_key",
                  "バックエンドで使用される秘密キー (sk_...)"
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeyManagement;